import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const inputCls =
  "w-full bg-white/[0.04] border border-white/15 rounded-md px-4 py-3.5 text-white placeholder:text-white/30 focus:outline-none focus:border-gold transition-colors duration-300";

function GoldHeading({ children }) {
  return <h2 className="text-gold font-semibold text-sm md:text-base pt-2">{children}</h2>;
}

export default function DeleteAccount() {
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Delete Your Account — Kannur TheyyamNow";
    const robots = document.createElement("meta");
    robots.setAttribute("name", "robots");
    robots.setAttribute("content", "noindex");
    robots.setAttribute("data-legal-page", "1");
    document.head.appendChild(robots);
    return () => robots.remove();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (sending || done) return;
    setSending(true);
    try {
      await api.post("/delete-account-request", { email, reason });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#141010] text-white font-sans" data-testid="delete-account-page">
      <main className="max-w-xl mx-auto px-6 py-16 md:py-20">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="flex flex-col items-center gap-3">
            <img src="/logo.png" alt="Kannur TheyyamNow" className="w-14 h-auto" />
            <p className="text-[11px] uppercase tracking-[0.35em] text-gold font-medium">Kannur TheyyamNow</p>
          </div>

          <h1 className="mt-10 text-3xl md:text-4xl font-bold tracking-tight" data-testid="delete-account-title">
            Delete your account
          </h1>
          <p className="mt-4 text-white/70 leading-relaxed">
            You can permanently delete your Kannur TheyyamNow account and personal data at any time.
          </p>

          <div className="mt-10 space-y-8">
            <section>
              <GoldHeading>Option 1 — In the app (instant)</GoldHeading>
              <ol className="mt-4 space-y-2.5 text-white/80 list-decimal pl-5 marker:text-white/40">
                <li>Open the Kannur TheyyamNow app and sign in.</li>
                <li>
                  Go to <strong className="text-white">Profile → Settings → Delete Account</strong>.
                </li>
                <li>Confirm. Your account is deleted immediately.</li>
              </ol>
            </section>

            <section>
              <GoldHeading>Option 2 — Request deletion here</GoldHeading>
              <div className="mt-4 border border-white/10 rounded-xl bg-white/[0.03] p-6 md:p-7">
                {done ? (
                  <div className="flex flex-col items-center text-center gap-4 py-6" data-testid="delete-account-success">
                    <CheckCircle2 className="text-gold" size={40} strokeWidth={1.5} />
                    <p className="text-lg font-semibold">Request received</p>
                    <p className="text-white/60 text-sm leading-relaxed max-w-sm">
                      We'll verify your email and delete your account within 30 days. You'll hear from us at the address you provided.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={submit} data-testid="delete-account-form" className="space-y-5">
                    <p className="text-white/70 text-sm leading-relaxed">
                      No access to the app? Enter the email you signed up with. We'll verify it and delete your account within 30 days.
                    </p>
                    <div>
                      <label htmlFor="del-email" className="block text-xs uppercase tracking-[0.15em] text-white/60 font-medium mb-2">
                        Account email *
                      </label>
                      <input
                        id="del-email"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        className={inputCls}
                        data-testid="delete-account-email-input"
                      />
                    </div>
                    <div>
                      <label htmlFor="del-reason" className="block text-xs uppercase tracking-[0.15em] text-white/60 font-medium mb-2">
                        Reason (optional)
                      </label>
                      <textarea
                        id="del-reason"
                        rows={4}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Tell us why you're leaving (optional)"
                        className={`${inputCls} resize-none`}
                        data-testid="delete-account-reason-input"
                      />
                    </div>
                    <motion.button
                      type="submit"
                      disabled={sending}
                      whileTap={{ scale: 0.98 }}
                      data-testid="delete-account-submit"
                      className="w-full bg-vermillion hover:bg-blood disabled:opacity-60 text-white font-semibold rounded-full py-4 transition-colors duration-300"
                    >
                      {sending ? "Sending…" : "Request account deletion"}
                    </motion.button>
                  </form>
                )}
              </div>
            </section>

            <section>
              <GoldHeading>What gets deleted</GoldHeading>
              <ul className="mt-4 space-y-2.5 text-white/80 list-disc pl-5 marker:text-gold">
                <li>Your account (name, email, profile photo) and sign-in sessions</li>
                <li>Your saved Theyyams, temples and events</li>
                <li>Your event submissions and suggestions, including attached photos</li>
                <li>Your device notification token</li>
              </ul>
            </section>

            <section>
              <GoldHeading>What may be kept</GoldHeading>
              <ul className="mt-4 space-y-2.5 text-white/80 list-disc pl-5 marker:text-gold">
                <li>Anonymous share counts (no personal data)</li>
                <li>Copies of submissions/suggestions already emailed to our team inbox are deleted on request</li>
              </ul>
            </section>

            <p className="text-white/50 text-sm pt-2">
              Questions? Email{" "}
              <a href="mailto:info@kannurtheyyamnow.com" className="text-gold underline underline-offset-4 hover:text-goldlight transition-colors duration-300">
                info@kannurtheyyamnow.com
              </a>{" "}
              ·{" "}
              <Link to="/privacy-policy" data-testid="delete-account-privacy-link" className="text-gold underline underline-offset-4 hover:text-goldlight transition-colors duration-300">
                Privacy Policy
              </Link>
            </p>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
